import React from 'react';
import {MapPin, Clock, Calendar, DollarSign} from 'lucide-react';
import {StatusBadge} from '@/components/UI/StatusBadge';
import {formatCurrency, formatDateTime, formatDuration, isNowInRange, parseAddress} from '@/utils/formatters';
import type {Order} from '@/types';

interface OrdersSectionProps {
    orders: Order[];
}

export const OrdersSection: React.FC<OrdersSectionProps> = ({orders}) => {
    if (orders.length === 0) {
        return (
            <div className="bg-white shadow rounded-lg p-6">
                <div className="text-center">
                    <MapPin className="mx-auto h-12 w-12 text-gray-400"/>
                    <h3 className="mt-2 text-sm font-medium text-gray-900">No order records</h3>
                    <p className="mt-1 text-sm text-gray-500">
                        This user has no order history.
                    </p>
                </div>
            </div>
        );
    }

    const totalCost = orders.reduce((sum, order) => sum + parseFloat(order.amount), 0);
    const activeParkings = orders.filter(p => p.order_for === 'Parking' && p.additional_params.expire_date > new Date().toISOString());
    const expiredParkings = orders.filter(p => p.order_for === 'Parking' && p.additional_params.expire_date < new Date().toISOString());
    return (
        <div className="space-y-6">
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-white overflow-hidden shadow rounded-lg">
                    <div className="p-5">
                        <div className="flex items-center">
                            <div className="flex-shrink-0">
                                <MapPin className="h-6 w-6 text-gray-400"/>
                            </div>
                            <div className="ml-5 w-0 flex-1">
                                <dl>
                                    <dt className="text-sm font-medium text-gray-500 truncate">
                                        Total Sessions
                                    </dt>
                                    <dd className="text-lg font-medium text-gray-900">
                                        {orders.length}
                                    </dd>
                                </dl>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="bg-white overflow-hidden shadow rounded-lg">
                    <div className="p-5">
                        <div className="flex items-center">
                            <div className="flex-shrink-0">
                                <Clock className="h-6 w-6 text-blue-400"/>
                            </div>
                            <div className="ml-5 w-0 flex-1">
                                <dl>
                                    <dt className="text-sm font-medium text-gray-500 truncate">
                                        Active Parkings
                                    </dt>
                                    <dd className="text-lg font-medium text-gray-900">
                                        {activeParkings.length}
                                    </dd>
                                </dl>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="bg-white overflow-hidden shadow rounded-lg">
                    <div className="p-5">
                        <div className="flex items-center">
                            <div className="flex-shrink-0">
                                <Clock className="h-6 w-6 text-blue-400"/>
                            </div>
                            <div className="ml-5 w-0 flex-1">
                                <dl>
                                    <dt className="text-sm font-medium text-gray-500 truncate">
                                        Expired Parkings
                                    </dt>
                                    <dd className="text-lg font-medium text-gray-900">
                                        {expiredParkings.length}
                                    </dd>
                                </dl>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="bg-white overflow-hidden shadow rounded-lg">
                    <div className="p-5">
                        <div className="flex items-center">
                            <div className="flex-shrink-0">
                                <DollarSign className="h-6 w-6 text-green-400"/>
                            </div>
                            <div className="ml-5 w-0 flex-1">
                                <dl>
                                    <dt className="text-sm font-medium text-gray-500 truncate">
                                        Total Spent
                                    </dt>
                                    <dd className="text-lg font-medium text-gray-900">
                                        {formatCurrency(totalCost)}
                                    </dd>
                                </dl>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Parking Sessions Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {orders.map((order) => (

                    <div key={order.id} className="bg-white shadow rounded-lg overflow-hidden">
                        <div className="p-6">
                            {/* Header */}
                            <div className="flex items-center justify-between mb-4">
                                <h4 className="text-lg font-medium text-gray-900">
                                    Order #{order.order_number}
                                </h4>
                                <StatusBadge status={order.order_state} size="sm"/>
                            </div>

                            {/* Location */}
                            <div className="flex items-start mb-3">
                                <MapPin className="h-5 w-5 text-gray-400 mt-0.5 mr-2 flex-shrink-0"/>
                                <div>
                                    <p className="text-sm font-medium text-gray-900">
                                        {parseAddress(order.additional_data).street}
                                    </p>
                                    {parseAddress(order.additional_data).city && (
                                        <p className="text-xs text-gray-500 mt-1">
                                            {`${parseAddress(order.additional_data).city} ${parseAddress(order.additional_data).district} ${parseAddress(order.additional_data).street}`}
                                        </p>
                                    )}
                                    {parseAddress(order.additional_data).identifier && (
                                        <p className="text-xs text-blue-600 mt-1">
                                            Zone: {parseAddress(order.additional_data).identifier}
                                        </p>
                                    )}
                                </div>
                            </div>

                            {/* Time Information */}
                            <div className="space-y-2 mb-4">
                                <div className="flex items-center text-sm text-gray-600">
                                    <Calendar className="h-4 w-4 mr-2"/>
                                    <span>Start date: {formatDateTime(order.additional_params.start_date)}</span>
                                </div>

                                {order.additional_params.expire_date && (
                                    <div className="flex items-center text-sm text-gray-600">
                                        <Calendar className="h-4 w-4 mr-2"/>
                                        <span>End date: {formatDateTime(order.additional_params.expire_date)}</span>
                                    </div>
                                )}
                                {isNowInRange(order.additional_params.start_date, order.additional_params.expire_date) &&
                                    (
                                        <div className="flex items-center text-sm text-blue-600">
                                            <Clock className="h-4 w-4 mr-2"/>
                                            <span>Ongoing session</span>
                                        </div>
                                    )}

                                {order.additional_params.unit && (
                                    <div className="flex items-center text-sm text-gray-600">
                                        <Clock className="h-4 w-4 mr-2"/>
                                        <span>Duration: {formatDuration(order.additional_params.unit * 60)}</span>
                                    </div>
                                )}
                            </div>

                            {/* Cost */}
                            <div className="pt-4 border-t border-gray-200">
                                <div className="flex items-center justify-between">
                                    <span className="text-sm font-medium text-gray-500">Cost</span>
                                    <span className="text-lg font-semibold text-gray-900">
                    {formatCurrency(parseFloat(order.amount))}
                  </span>
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default OrdersSection;
